import { useCallback, useEffect, useRef, useState } from "react";

import { fetchPortfolioAnalysis } from "../services/portfolioAnalysisApi";

export function usePortfolioAnalysis({
  scenarioIds,
  simulations,
  seed,
  objective,
  optimizationMode = "LEGACY",
  approvedBudget = null,
  enabled,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);
  const idsKey = [...new Set((scenarioIds || []).map(Number))].sort((a, b) => a - b).join(",");

  const runAnalysis = useCallback(async () => {
    if (!enabled || !idsKey) return null;
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPortfolioAnalysis({
        scenarioIds: idsKey.split(",").map(Number), simulations, seed, objective,
        optimizationMode, approvedBudget,
      });
      if (requestId.current === currentRequest) setData(result);
      return result;
    } catch (requestError) {
      if (requestId.current === currentRequest) setError(requestError);
      return null;
    } finally {
      if (requestId.current === currentRequest) setLoading(false);
    }
  }, [approvedBudget, enabled, idsKey, objective, optimizationMode, seed, simulations]);

  const resetAnalysis = useCallback(() => {
    requestId.current += 1;
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled || !idsKey) {
      resetAnalysis();
      return undefined;
    }
    runAnalysis();
    return () => { requestId.current += 1; };
  }, [enabled, idsKey, resetAnalysis, runAnalysis]);

  return { data, loading, error, available: Boolean(data), runAnalysis, resetAnalysis };
}
